import { useRouter } from 'next/router';
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';

import { GenerationErrorModal } from '../../components/dynamicPages/CreatePage/CreateFlow/components/GenerationErrorModal';

type ShowGenerationErrorArgs = {
  /**
   * Re-runs the generation that failed. The generation hooks build this so it
   * replays the *exact* original `mutate(variables, options)` call — including
   * the per-call `onSuccess` that redirects into the editor — so a successful
   * "Try again" behaves identically to a first-try success.
   */
  onRetry: () => void;
  /**
   * True when the request never got a response (dropped connection / timeout) —
   * the case where the lesson usually still finishes on the backend. False for
   * server errors (4xx/5xx), where nothing was created. Drives the popup copy.
   */
  isConnectionError: boolean;
};

type GenerationErrorContextValue = {
  showGenerationError: (args: ShowGenerationErrorArgs) => void;
};

// Default no-ops so a hook used outside the provider degrades to "no popup"
// rather than crashing generation. The provider is mounted app-wide in _app.tsx.
const GenerationErrorContext = createContext<GenerationErrorContextValue>({
  showGenerationError: () => undefined,
});

export const useGenerationError = () => useContext(GenerationErrorContext);

/**
 * Owns the single, app-wide "generation failed" popup (CURI-362). The two
 * generation transports (`useConsumeGeneratorAsNewPresentation` and
 * `useGenerateActivities`) call `showGenerationError` from their `onError`, so
 * every generator gets the popup for free with no per-form wiring.
 */
export const GenerationErrorProvider = ({ children }: { children: React.ReactNode }) => {
  const [errorState, setErrorState] = useState<ShowGenerationErrorArgs | null>(null);

  const showGenerationError = useCallback((args: ShowGenerationErrorArgs) => {
    setErrorState(args);
  }, []);

  const close = useCallback(() => setErrorState(null), []);

  // Safety net: this popup is app-wide, so any navigation while it's open should
  // dismiss it (otherwise it lingers on the destination page — e.g. after "Go to
  // My Lessons"). Covers navigations that don't route through `close`.
  const router = useRouter();
  useEffect(() => {
    const dismiss = () => setErrorState(null);
    router.events.on('routeChangeComplete', dismiss);
    return () => router.events.off('routeChangeComplete', dismiss);
  }, [router.events]);

  const handleRetry = useCallback(() => {
    errorState?.onRetry();
    setErrorState(null);
  }, [errorState]);

  const value = useMemo(() => ({ showGenerationError }), [showGenerationError]);

  return (
    <GenerationErrorContext.Provider value={value}>
      {children}
      <GenerationErrorModal
        open={!!errorState}
        isConnectionError={errorState?.isConnectionError ?? true}
        onRetry={handleRetry}
        onClose={close}
      />
    </GenerationErrorContext.Provider>
  );
};
